import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { PhaseSubTabs } from '../parts/PhaseSubTabs';
import type { ExpectedField, ImportBatchRow } from '@discovr/types';
import { DuplicateGroupCard } from './DuplicateGroupCard';
import { NeedsFixingRowCard } from './NeedsFixingRowCard';
import {
  duplicateGroups,
  needsFixingRows,
  needsSheetDecision,
  isSheetReviewComplete,
  sheetUndecidedCount,
} from './importWizardHelpers';
import { WizardFooter } from './ImportWizardFooterSlot';
import { RowNumberBadge } from './ImportRowBadge';

interface ImportSheetIssuesStepProps {
  rows: ImportBatchRow[];
  expectedFields: ExpectedField[];
  /** Gates NeedsFixingRowCard's username wrong-platform-link live check —
   *  see clientFieldError. */
  campaignPlatform?: string;
  busy: boolean;
  onResolve: (rowNumber: number, action: 'keep' | 'drop' | 'fixed', values?: Record<string, string>) => Promise<void>;
  onBack: () => Promise<void>;
  onContinue: () => Promise<void>;
}

type SheetIssuesTab = 'all' | 'duplicates' | 'fixing' | 'undecided';

/** A duplicate group still needs a decision until one member has been kept
 *  (which resolves every sibling to 'drop' server-side in the same call). */
function groupUndecided(members: ImportBatchRow[]): boolean {
  return !members.some((m) => m.sheetResolution === 'keep');
}

export function ImportSheetIssuesStep({ rows, expectedFields, campaignPlatform, busy, onResolve, onBack, onContinue }: ImportSheetIssuesStepProps) {
  const groups = duplicateGroups(rows);
  const fixingUnresolved = needsFixingRows(rows);
  // The "Needs fixing" tab's LIST includes a row even after it's been
  // skipped (sheetResolution 'drop') or successfully fixed (sheetResolution
  // 'fixed', validationErrors now empty) — needsFixingRows excludes both
  // once resolved, which is right for the tab's count/badge, but the admin
  // still needs to see the row in the list (with a Skipped/Fixed badge) to
  // change their mind via "Fix here".
  const fixingAll = rows.filter((r) => Object.keys(r.validationErrors).length > 0 || r.sheetResolution === 'fixed');
  const cleanRows = rows.filter(
    (r) => r.duplicateGroupSize <= 1 && Object.keys(r.validationErrors).length === 0 && r.sheetResolution !== 'fixed',
  );
  const [tab, setTab] = useState<SheetIssuesTab>(groups.size > 0 ? 'duplicates' : fixingUnresolved.length > 0 ? 'fixing' : 'all');
  const complete = isSheetReviewComplete(rows);

  const undecidedGroups = [...groups.entries()].filter(([, members]) => groupUndecided(members));
  const undecidedFixing = fixingUnresolved.filter(needsSheetDecision);
  const undecidedCount = sheetUndecidedCount(rows);

  return (
    <div className="flex flex-col gap-4">
      <PhaseSubTabs
        variant="underline"
        active={tab}
        tabs={[
          { key: 'all', label: 'All', count: rows.length },
          { key: 'duplicates', label: 'Duplicates', count: groups.size },
          { key: 'fixing', label: 'Needs fixing', count: fixingUnresolved.length },
          { key: 'undecided', label: 'Undecided', count: undecidedCount },
        ]}
        onChange={setTab}
      />

      {tab === 'all' && (
        <div className="flex flex-col gap-4 pt-3">
          {groups.size > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-caption-medium text-muted-foreground">Duplicates</h4>
              <div className="flex flex-col gap-3">
                {[...groups.entries()].map(([usernameKey, members]) => (
                  <DuplicateGroupCard key={usernameKey} usernameKey={usernameKey} members={members} busy={busy} onResolve={onResolve} />
                ))}
              </div>
            </div>
          )}
          {fixingAll.length > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-caption-medium text-muted-foreground">Needs fixing</h4>
              <div className="flex flex-col gap-3">
                {fixingAll.map((row) => (
                  <NeedsFixingRowCard key={row.rowNumber} row={row} expectedFields={expectedFields} campaignPlatform={campaignPlatform} busy={busy} onResolve={onResolve} />
                ))}
              </div>
            </div>
          )}
          {cleanRows.length > 0 && (
            <div className="flex flex-col gap-2">
              <h4 className="text-caption-medium text-muted-foreground">Clean</h4>
              <div className="divide-y divide-border overflow-hidden rounded-lg border border-border">
                {cleanRows.map((row) => {
                  const username = row.values.username || row.usernameKey || '—';
                  return (
                    <div key={row.rowNumber} className="flex items-center gap-3 bg-card px-3 py-2 transition-colors hover:bg-muted/40">
                      <RowNumberBadge n={row.rowNumber} />
                      <span className="min-w-0 flex-1 truncate text-body-sm-medium text-foreground">{username}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {tab === 'duplicates' && (
        <div className="flex flex-col gap-3 pt-3">
          {groups.size === 0 && (
            <p className="text-body-sm-regular text-muted-foreground">No duplicate usernames in this sheet.</p>
          )}
          {[...groups.entries()].map(([usernameKey, members]) => (
            <DuplicateGroupCard key={usernameKey} usernameKey={usernameKey} members={members} busy={busy} onResolve={onResolve} />
          ))}
        </div>
      )}

      {tab === 'fixing' && (
        <div className="flex flex-col gap-3 pt-3">
          {fixingAll.length === 0 && (
            <p className="text-body-sm-regular text-muted-foreground">No rows need fixing.</p>
          )}
          {fixingAll.map((row) => (
            <NeedsFixingRowCard key={row.rowNumber} row={row} expectedFields={expectedFields} campaignPlatform={campaignPlatform} busy={busy} onResolve={onResolve} />
          ))}
        </div>
      )}

      {tab === 'undecided' && (
        <div className="flex flex-col gap-3 pt-3">
          {undecidedGroups.length === 0 && undecidedFixing.length === 0 && (
            <p className="text-body-sm-regular text-muted-foreground">Nothing left to decide.</p>
          )}
          {undecidedGroups.map(([usernameKey, members]) => (
            <DuplicateGroupCard key={usernameKey} usernameKey={usernameKey} members={members} busy={busy} onResolve={onResolve} />
          ))}
          {undecidedFixing.map((row) => (
            <NeedsFixingRowCard key={row.rowNumber} row={row} expectedFields={expectedFields} campaignPlatform={campaignPlatform} busy={busy} onResolve={onResolve} />
          ))}
        </div>
      )}

      <WizardFooter>
        <div className="flex w-full items-center justify-between">
          <Button type="button" variant="outline" size="lg" disabled={busy} onClick={() => { onBack().catch(() => {}); }}>
            Back
          </Button>
          <Button
            type="button"
            variant="default"
            size="lg"
            loading={busy}
            disabled={!complete}
            onClick={() => { onContinue().catch(() => {}); }}
          >
            Continue
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}
